import React from 'react';
import { UsersIcon, XIcon } from '@/components/icons';
import { formatCurrencyForDisplay, formatTimeWithNextDay } from '@/lib/utils';
import type { TableAvailabilitySlot } from '@/features/booking/types/tableBookingTypes';
import SwipeModal from '@/components/ui/SwipeModal';

interface SlotSelectorModalProps {
  isOpen: boolean;
  onClose: () => void;
  tableNumber: string;
  tableSize: number;
  slots: TableAvailabilitySlot[];
  onSlotSelect: (slot: TableAvailabilitySlot) => void;
  businessTimeZone?: string;
  currency?: string;
  eventStartTime?: string | null;
}

const SlotSelectorModal: React.FC<SlotSelectorModalProps> = ({
  isOpen,
  onClose,
  tableNumber,
  tableSize,
  slots,
  onSlotSelect,
  businessTimeZone,
  currency,
  eventStartTime
}) => {
  const formatPrice = (amount: number) => currency 
    ? formatCurrencyForDisplay(amount, currency) 
    : `${(amount / 100).toFixed(2)}`;

  return (
    <SwipeModal isOpen={isOpen} onClose={onClose}>
          <div className="p-6 pb-4 border-b border-gray-800 flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold">Table {tableNumber}</h2>
              <div className="flex items-center text-blue-400 mt-1">
                <UsersIcon />
                <span className="ml-2 text-sm">Up to {tableSize} Guests</span>
              </div>
            </div>
            <button 
              onClick={onClose}
              className="p-2 hover:bg-gray-800 rounded-lg transition-colors"
            >
              <XIcon />
            </button>
          </div>
          
          <div className="flex-1 overflow-y-auto scrollbar-hide">
            <div className="p-6 pt-4">
              <p className="font-semibold text-gray-300 mb-4">
                Select an available time ({slots.length} options):
              </p>
              
              {slots.length === 0 ? (
                <div className="text-center py-12">
                  <div className="text-gray-500 mb-2">No available time slots</div>
                  <div className="text-sm text-gray-600">Please try a different date</div>
                </div>
              ) : (
                <div className="space-y-3 pb-6">
                  {slots.map(slot => (
                    <button 
                      key={slot.start_time}
                      onClick={() => onSlotSelect(slot)}
                      className="w-full p-4 rounded-xl border border-gray-700 bg-gray-800/50 hover:bg-gray-700/50 hover:border-blue-500 hover:shadow-lg hover:shadow-blue-500/10 transition-all duration-200 group"
                    >
                      <div className="flex justify-between items-center">
                        <div className="text-left flex-1">
                          <div className="text-lg font-bold text-white group-hover:text-blue-400 transition-colors">
                            {(() => {
                              const timeStr = formatTimeWithNextDay(slot.start_time, eventStartTime || '', businessTimeZone);
                              const hasNextDay = timeStr.includes('+1');
                              const baseTime = timeStr.replace('+1', '');
                              return (
                                <>
                                  {baseTime}
                                  {hasNextDay && <sup className="ml-0.5 text-[10px] text-blue-400">+1</sup>}
                                </>
                              );
                            })()}
                          </div>
                          <div className="text-sm font-medium text-gray-300 mt-1">
                            {slot.event_booking_category.booking_category.name}
                          </div>
                          <div className="text-xs text-gray-400 mt-1 line-clamp-2">
                            {slot.event_booking_category.booking_category.description}
                          </div>
                        </div>
                        <div className="text-right ml-4">
                          <div className="font-bold text-xl text-white group-hover:text-blue-400 transition-colors">
                            {formatPrice(slot.event_booking_category.price)}
                          </div>
                          <div className="text-xs text-gray-500 mt-1">per table</div>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
          
          <div className="p-6 pt-4 border-t border-gray-800 mt-auto">
            <div className="text-center text-sm text-gray-500">
              Click on a time slot to select it
            </div>
          </div>
    </SwipeModal>
  );
};

export default SlotSelectorModal;
